// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { some, Step } from "@guestsupply/App/Widgets/Stepper/helpers";
import { doScroll } from "@guestsupply/XDL/Utilities";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { createContext, useContext, useRef, useState } from "react";

export interface StepperProvider {
    initialStep?: number;
    steps: Step[];
    allowStepClick?: boolean;
}

export interface StepperContextData {
    steps: Step[];
    currentStep: number;
    maxStep: number;
    allowStepClick: boolean;
    setCurrentStep: (step: number) => void;
    moveToStepHandler: (step: number) => void;
    setMaxStep: (step: number) => void;
    preventStepperChange: (hook: any, position: number) => void;
    showStep: (step: number) => boolean;
}

export type HasStepperContext = Pick<StepperContextData, "currentStep">;

const stepperContextData: StepperContextData = {
    steps: [],
    currentStep: 1,
    maxStep: 1,
    setCurrentStep: (step: number) => {},
    moveToStepHandler: (step: number) => {},
    setMaxStep: (step: number) => {},
    preventStepperChange: (hook: any, position: number) => {},
    showStep: (step: number) => false,
    allowStepClick: false,
};

export const StepperContext = createContext<StepperContextData>(stepperContextData);

const StepperContextProvider: React.FC<StepperProvider> = ({
    initialStep = 1,
    children,
    steps,
    allowStepClick = false,
}) => {
    const history = useHistory();
    const [currentStep, doSetCurrentStep] = useState<number>(initialStep);
    const [maxStep, doSetMaxStep] = useState<number>(initialStep);
    const validationHookRef = useRef<any[]>([]);
    const shellContext = useContext(ShellContext);

    const setMaxStep = (state: number) => {
        doSetMaxStep(state);
    };

    const showStep = (step: number) => shellContext.isEditing || currentStep === step;

    // To update currentStep value to nextStep step
    const setCurrentStep = async (nextStep: number) => {
        const prevent = await some(validationHookRef.current, (item: any) => {
            const hook = item.hook.current(currentStep, nextStep);
            // check is already a promise and return
            if (typeof hook?.then === "function") {
                return hook;
            }
            return Promise.resolve(!!hook);
        });
        if (prevent) {
            return;
        }
        if (currentStep > maxStep) {
            setMaxStep(currentStep);
        }
        doSetCurrentStep(nextStep);
    };

    const preventStepperChange = (hook: any, position = 1) => {
        const vh = validationHookRef.current;
        const index = vh.findIndex((f: any) => f.position === position);
        if (index !== -1) {
            vh.splice(index, 1);
        }
        vh.push({ hook, position });
        vh.sort((a, b) => a.position - b.position);
    };

    // To move to any step
    const moveToStep = (moveToNewStep: number) => {
        if (moveToNewStep >= currentStep && !allowStepClick) {
            return;
        }
        const newStep = steps.find(s => s.index === moveToNewStep);
        if (!newStep) {
            return;
        }
        if (newStep.url) {
            history.push(newStep.url);
        }
        setCurrentStep?.(moveToNewStep);
        doScroll(true);
    };

    return (
        <StepperContext.Provider
            value={{
                steps,
                currentStep,
                setCurrentStep,
                maxStep,
                setMaxStep,
                preventStepperChange,
                moveToStepHandler: moveToStep,
                showStep,
                allowStepClick,
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export default StepperContextProvider;

export function withStepperContext<P extends HasStepperContext>(Component: React.ComponentType<P>) {
    return function StepperComponent(props: Omit<P, keyof HasStepperContext>) {
        return (
            <StepperContext.Consumer>
                {value => <Component {...(props as P)} currentStep={value.currentStep} />}
            </StepperContext.Consumer>
        );
    };
}
